import dayjs, { Dayjs } from "dayjs";
import type {
  SlotLength,
  VenueAvailabilityWindow,
  VenueBlock,
  VenueBooking,
  VenueHireSettings,
  VenueSlot,
} from "./types";

export const DATE_FORMAT = "YYYY-MM-DD";
export const TIME_FORMAT = "HH:mm";

export const venueHirePaymentDeadlineNotice =
  "You have 12 hours to complete payment. If payment is not received in time, your booking will be cancelled and the slot will become available for others to book.";

const VENUE_HIRE_REFUND_EMAIL = "matt@easyagility.co.uk";

export const isValidVenueBooking = (
  booking: unknown,
): booking is VenueBooking => {
  if (!booking || typeof booking !== "object") return false;
  const candidate = booking as Partial<VenueBooking>;
  return (
    typeof candidate.id === "number" &&
    Number.isFinite(candidate.id) &&
    typeof candidate.date === "string" &&
    candidate.date.length > 0 &&
    typeof candidate.start_time === "string" &&
    candidate.start_time.length > 0 &&
    typeof candidate.end_time === "string" &&
    candidate.end_time.length > 0 &&
    typeof candidate.price === "number" &&
    Number.isFinite(candidate.price)
  );
};

export const normalizeVenueBookings = (data: unknown): VenueBooking[] => {
  if (!Array.isArray(data)) return [];
  return data.filter(isValidVenueBooking).map((booking) => ({
    ...booking,
    slot_length: getVenueBookingSlotLength(booking),
    paid: Boolean(booking.paid),
  }));
};

export const getVenueBookingSlotLength = (
  booking: Pick<VenueBooking, "slot_length" | "start_time" | "end_time">,
): SlotLength => {
  if (
    booking.slot_length === 30 ||
    booking.slot_length === 60 ||
    booking.slot_length === 90
  ) {
    return booking.slot_length;
  }
  return inferSlotLength(booking.start_time, booking.end_time);
};

export const getVenueBookingStart = (
  booking: Pick<VenueBooking, "date" | "start_time">,
): Dayjs => dayjs(`${booking.date} ${booking.start_time}`, `${DATE_FORMAT} ${TIME_FORMAT}`);

export const isVenueBookingInPast = (
  booking: Pick<VenueBooking, "date" | "start_time">,
): boolean => {
  const start = getVenueBookingStart(booking);
  return !start.isValid() || start.isBefore(dayjs());
};

export const filterUpcomingVenueBookings = (
  bookings: VenueBooking[],
): VenueBooking[] =>
  bookings.filter(
    (booking) => isValidVenueBooking(booking) && !isVenueBookingInPast(booking),
  );

export const formatVenueBookingPrice = (
  price: number | null | undefined,
): string => {
  if (typeof price !== "number" || !Number.isFinite(price)) {
    return "—";
  }
  return `£${price.toFixed(2)}`;
};

export const buildVenueHireRefundMailto = (params: {
  clubName: string;
  date: string;
  startTime: string;
  endTime: string;
  price: number;
  invoiceId?: number;
}): string => {
  const clubName = params.clubName?.trim() || "your club";
  const slotLabel =
    params.date && params.startTime && params.endTime
      ? formatSlotRange(params.date, params.startTime, params.endTime)
      : "Venue hire booking";
  const priceLabel = formatVenueBookingPrice(params.price);
  const paymentRef = params.invoiceId
    ? `INV-${params.invoiceId}`
    : "not available";
  const subject = `Venue hire cancellation and refund request – ${clubName}`;
  const body = [
    "Hi Matt,",
    "",
    "I would like to cancel my venue hire booking and request a refund.",
    "",
    `Club: ${clubName}`,
    `Booking: ${slotLabel}`,
    `Price: ${priceLabel}`,
    `Payment reference: ${paymentRef}`,
    "",
    "Please let me know if you need any further information.",
    "",
    "Thank you.",
  ].join("\n");

  return `mailto:${VENUE_HIRE_REFUND_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

export const formatDate = (date: string): string =>
  dayjs(date).format("ddd D MMM YYYY");

export const formatTime = (time: string): string => dayjs(time, TIME_FORMAT).format("HH:mm");

export const formatSlotRange = (
  date: string,
  startTime: string,
  endTime: string,
): string =>
  `${formatDate(date)}, ${formatTime(startTime)} – ${formatTime(endTime)}`;

export const toDateString = (value: Dayjs): string => value.format(DATE_FORMAT);

export const toTimeString = (value: Dayjs): string => value.format(TIME_FORMAT);

export const slotRangeMinutes = (startTime: string, endTime: string): number => {
  const start = dayjs(startTime, TIME_FORMAT);
  const end = dayjs(endTime, TIME_FORMAT);
  return end.diff(start, "minute");
};

export const isValidTimeRange = (startTime: string, endTime: string): boolean =>
  slotRangeMinutes(startTime, endTime) > 0;

export const rangesOverlap = (
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string,
): boolean => {
  const aStartMin = dayjs(aStart, TIME_FORMAT);
  const aEndMin = dayjs(aEnd, TIME_FORMAT);
  const bStartMin = dayjs(bStart, TIME_FORMAT);
  const bEndMin = dayjs(bEnd, TIME_FORMAT);
  return aStartMin.isBefore(bEndMin) && bStartMin.isBefore(aEndMin);
};

export const eachDateInRange = (fromDate: string, toDate: string): string[] => {
  const dates: string[] = [];
  let current = dayjs(fromDate);
  const end = dayjs(toDate);
  while (current.isBefore(end) || current.isSame(end, "day")) {
    dates.push(current.format(DATE_FORMAT));
    current = current.add(1, "day");
  }
  return dates;
};

export const windowOverlapsExisting = (
  date: string,
  startTime: string,
  endTime: string,
  existing: VenueAvailabilityWindow[],
  excludeId?: number,
): boolean =>
  existing.some(
    (window) =>
      window.id !== excludeId &&
      window.date === date &&
      rangesOverlap(startTime, endTime, window.start_time, window.end_time),
  );

export const windowOverlapsExistingInRange = (
  fromDate: string,
  toDate: string,
  startTime: string,
  endTime: string,
  existing: VenueAvailabilityWindow[],
  excludeId?: number,
): string | null => {
  for (const date of eachDateInRange(fromDate, toDate)) {
    if (windowOverlapsExisting(date, startTime, endTime, existing, excludeId)) {
      return date;
    }
  }
  return null;
};

export const blockOverlapsExisting = (
  date: string,
  startTime: string,
  endTime: string,
  existing: VenueBlock[],
  excludeId?: number,
): boolean =>
  existing.some(
    (block) =>
      block.id !== excludeId &&
      block.date === date &&
      rangesOverlap(startTime, endTime, block.start_time, block.end_time),
  );

export const slotLengthFitsRange = (
  startTime: string,
  endTime: string,
  slotLength: SlotLength,
): boolean => {
  const minutes = slotRangeMinutes(startTime, endTime);
  return minutes > 0 && minutes % slotLength === 0;
};

export const getRollingDateRange = (weeks = 4): { from: string; to: string } => {
  const from = dayjs().format(DATE_FORMAT);
  const to = dayjs().add(weeks, "week").format(DATE_FORMAT);
  return { from, to };
};

export const groupByDate = <T extends { date: string }>(
  items: T[],
): Record<string, T[]> =>
  items.reduce<Record<string, T[]>>((acc, item) => {
    if (!acc[item.date]) acc[item.date] = [];
    acc[item.date].push(item);
    return acc;
  }, {});

export type SlotDateSummary = {
  hasSlots: boolean;
  hasAvailable: boolean;
  availableCount: number;
};

export type SlotLengthFilter = "all" | SlotLength;

export const slotLengthOf = (slot: VenueSlot): SlotLength =>
  slot.slot_length ?? inferSlotLength(slot.start_time, slot.end_time);

export const venueSlotKey = (slot: VenueSlot): string => {
  const length = slotLengthOf(slot);
  return `${slot.date}-${slot.start_time}-${slot.end_time}-${length}`;
};

export const filterSlotsByLength = (
  slots: VenueSlot[],
  filter: SlotLengthFilter,
): VenueSlot[] => {
  if (filter === "all") return slots;
  return slots.filter((slot) => slotLengthOf(slot) === filter);
};

export const preferredSlotLengthFilter = (
  slots: VenueSlot[],
): SlotLengthFilter => {
  if (
    slots.some(
      (slot) => slotLengthOf(slot) === 60 && slot.status === "available",
    )
  ) {
    return 60;
  }
  return "all";
};

export const summariseSlotsByDate = (
  slots: VenueSlot[],
): Record<string, SlotDateSummary> => {
  const summary: Record<string, SlotDateSummary> = {};
  for (const slot of slots) {
    if (!summary[slot.date]) {
      summary[slot.date] = {
        hasSlots: true,
        hasAvailable: false,
        availableCount: 0,
      };
    }
    if (slot.status === "available") {
      summary[slot.date].hasAvailable = true;
      summary[slot.date].availableCount += 1;
    }
  }
  return summary;
};

export const firstDateWithAvailableSlots = (
  slots: VenueSlot[],
): string | null => {
  const dates = Array.from(new Set(slots.map((s) => s.date))).sort();
  for (const date of dates) {
    if (slots.some((s) => s.date === date && s.status === "available")) {
      return date;
    }
  }
  return dates[0] ?? null;
};

export const firstDateWithItems = <T extends { date: string }>(
  items: T[],
): string | null => {
  const dates = Array.from(new Set(items.map((item) => item.date))).sort();
  return dates[0] ?? null;
};

export const isSlotTakenError = (err: unknown): boolean => {
  const axiosErr = err as { response?: { status?: number } };
  return axiosErr.response?.status === 409;
};

export const isInvalidVoucherError = (err: unknown): boolean => {
  const axiosErr = err as { response?: { status?: number; data?: string } };
  if (axiosErr.response?.status !== 400) return false;
  const data = axiosErr.response?.data;
  return typeof data === "string" && data.toLowerCase().includes("invalid voucher code");
};

export const formatAvailabilityPrices = (window: {
  price_per_30_min: number;
  price_per_60_min: number;
  price_per_90_min: number;
}): string => {
  const parts: string[] = [];
  if (window.price_per_30_min > 0) {
    parts.push(`30 min · £${window.price_per_30_min.toFixed(2)}`);
  }
  if (window.price_per_60_min > 0) {
    parts.push(`60 min · £${window.price_per_60_min.toFixed(2)}`);
  }
  if (window.price_per_90_min > 0) {
    parts.push(`90 min · £${window.price_per_90_min.toFixed(2)}`);
  }
  return parts.length > 0 ? parts.join(" · ") : "No prices set";
};

export const slotDurationMinutes = (
  startTime: string,
  endTime: string,
): number => slotRangeMinutes(startTime, endTime);

export const inferSlotLength = (
  startTime: string,
  endTime: string,
): SlotLength => {
  const minutes = slotRangeMinutes(startTime, endTime);
  if (minutes >= 90) return 90;
  if (minutes >= 60) return 60;
  return 30;
};

export const normalizeVenueHireSettings = (
  settings?: Partial<VenueHireSettings> & { default_price_per_slot?: number },
): VenueHireSettings => ({
  enabled: settings?.enabled ?? false,
  price_per_30_min:
    settings?.price_per_30_min ?? settings?.default_price_per_slot ?? 8,
  price_per_60_min:
    settings?.price_per_60_min ?? settings?.default_price_per_slot ?? 12,
  price_per_90_min: settings?.price_per_90_min ?? 18,
  default_slot_length: settings?.default_slot_length ?? 60,
  notes: settings?.notes,
});

export const normalizeAvailabilityWindow = (
  window: Partial<VenueAvailabilityWindow> & {
    slot_length?: SlotLength;
    price_per_slot?: number;
  },
): Pick<
  VenueAvailabilityWindow,
  "price_per_30_min" | "price_per_60_min" | "price_per_90_min"
> => ({
  price_per_30_min:
    window.price_per_30_min ??
    (window.slot_length === 30 ? window.price_per_slot : undefined) ??
    8,
  price_per_60_min:
    window.price_per_60_min ??
    (window.slot_length === 60 ? window.price_per_slot : undefined) ??
    12,
  price_per_90_min:
    window.price_per_90_min ??
    (window.slot_length === 90 ? window.price_per_slot : undefined) ??
    18,
});
